<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import {
  fetchEmailManagementStatus,
  fetchEmailManagementAdmins,
  fetchEmailThreadsPage,
  fetchEmailThread,
  fetchEmailReplySuggestions,
  fetchEmailAttachment,
  sendEmailReply,
  sendNewEmail,
  setEmailThreadAssignees,
  deleteEmailThread,
  restoreEmailThread,
  fetchEmailMessagesTranslation,
  fetchFreeTextTranslation,
} from "@/services/backofficeClient";
import { ROLES } from "@/services/acl";
import UserDetailsPanel from "@/components/UserDetailsPanel.vue";
import RichTextEditor from "@/components/RichTextEditor.vue";
import {
  finalizeEmailHtml,
  htmlToPlainText,
  plainTextToHtml,
} from "@/utils/emailBody";

const THREADS_PAGE_SIZE = 25;
const TRANSLATION_MESSAGE_LIMIT = 30;
const TRANSLATION_LANGUAGES = [
  { code: "English", label: "English" },
  { code: "Persian", label: "Persian" },
];

const store = useStore();
const route = useRoute();
const router = useRouter();
const canReply = computed(() => store.getters.hasRole(ROLES.EMAIL_MANAGEMENT_REPLY));
const canDelete = computed(() => store.getters.hasRole(ROLES.EMAIL_MANAGEMENT_DELETE));
const canViewAll = computed(() => store.getters.hasRole(ROLES.EMAIL_MANAGEMENT_VIEW_ALL));
const canAssign = computed(() => store.getters.hasRole(ROLES.EMAIL_MANAGEMENT_ASSIGN));
const canTranslate = computed(() => store.getters.hasRole(ROLES.TRANSLATION_INDEX));
const currentAdminId = computed(() => store.getters.currentAdmin?.id || null);

// ---------------------------------------------------------------------------
// Webhook configuration status
// ---------------------------------------------------------------------------
const statusChecked = ref(false);
const configured = ref(false);
const statusError = ref("");
const senders = ref([]);
const defaultSender = ref("");

const checkStatus = async () => {
  statusError.value = "";
  try {
    const result = await fetchEmailManagementStatus();
    configured.value = Boolean(result?.configured);
    senders.value = result?.senders || [];
    defaultSender.value = result?.defaultSender || senders.value[0]?.email || "";
  } catch (err) {
    statusError.value = err?.message || "Failed to check Email Inbox status.";
  } finally {
    statusChecked.value = true;
  }
};

const sameEmail = (a, b) =>
  Boolean(a) && Boolean(b) && a.trim().toLowerCase() === b.trim().toLowerCase();

const senderLabel = (sender) =>
  sender.name ? `${sender.name} <${sender.email}>` : sender.email;

// ---------------------------------------------------------------------------
// Assignable admins
// ---------------------------------------------------------------------------
const admins = ref([]);
const adminsById = computed(() => {
  const map = {};
  admins.value.forEach((admin) => {
    map[admin.id] = admin;
  });
  return map;
});

const loadAdmins = async () => {
  try {
    const payload = await fetchEmailManagementAdmins();
    admins.value = (payload?.items || []).map((admin) => ({
      id: admin.id,
      name: admin.name || admin.email || admin.id,
      email: admin.email || "",
    }));
  } catch (err) {
    admins.value = [];
  }
};

const adminLabel = (adminId) => {
  if (!adminId) return "Unassigned";
  return adminsById.value[adminId]?.name || adminId;
};

const assigneeLabel = (assignee) =>
  assignee?.name || assignee?.email || adminLabel(assignee?.id);

// ---------------------------------------------------------------------------
// Assignee filter — `null` means "Unassigned". Defaults to unassigned threads
// plus the ones assigned to the current admin; picking anyone else needs
// EMAIL_MANAGEMENT_VIEW_ALL, which the backend enforces too.
// ---------------------------------------------------------------------------
const defaultAssignedAdminIds = computed(() => {
  const ids = [null];
  if (currentAdminId.value) ids.push(currentAdminId.value);
  return ids;
});
const filterAssignedAdminIds = ref([...defaultAssignedAdminIds.value]);
const assignedFilterMenuOpen = ref(false);

const isAssignedFilterDefault = computed(() => {
  const current = new Set(filterAssignedAdminIds.value.map((v) => v ?? "null"));
  const def = new Set(defaultAssignedAdminIds.value.map((v) => v ?? "null"));
  if (current.size !== def.size) return false;
  for (const value of current) {
    if (!def.has(value)) return false;
  }
  return true;
});

const isAssignedFilterSelected = (value) =>
  filterAssignedAdminIds.value.includes(value);

const toggleAssignedFilterValue = (value) => {
  filterAssignedAdminIds.value = isAssignedFilterSelected(value)
    ? filterAssignedAdminIds.value.filter((v) => v !== value)
    : [...filterAssignedAdminIds.value, value];
  loadThreads(1);
};

const toggleAssignedFilterMenu = () => {
  assignMenuOpen.value = false;
  translateMenuOpen.value = false;
  assignedFilterMenuOpen.value = !assignedFilterMenuOpen.value;
};

const resetAssignedFilter = () => {
  filterAssignedAdminIds.value = [...defaultAssignedAdminIds.value];
  loadThreads(1);
};

const clearAssignedFilter = () => {
  filterAssignedAdminIds.value = [];
  loadThreads(1);
};

const assignedFilterLabel = computed(() => {
  if (filterAssignedAdminIds.value.length === 0) {
    return canViewAll.value ? "All" : "Unassigned + me";
  }
  return filterAssignedAdminIds.value
    .map((value) =>
      value !== null && value === currentAdminId.value ? "Me" : adminLabel(value)
    )
    .join(", ");
});

// ---------------------------------------------------------------------------
// Thread list state
// ---------------------------------------------------------------------------
const items = ref([]);
const pagination = ref({ page: 1, pageSize: THREADS_PAGE_SIZE, totalItems: 0, totalPages: 1 });
const currentPage = ref(1);
const threadsLoading = ref(false);
const threadsError = ref("");
const sortOrder = ref("newest"); // "newest" | "oldest"
const includeDeleted = ref(false);

const parseThreadSummary = (raw) => ({
  id: raw.id,
  subject: raw.subject || "(no subject)",
  participant: raw.participant || null,
  mailbox: raw.mailbox || "",
  messageCount: raw.messageCount || 0,
  isDeleted: raw.isDeleted || false,
  deletedAt: raw.deletedAt || null,
  assignedAdmins: raw.assignedAdmins || [],
  lastMessageAt: raw.lastMessageAt ? new Date(raw.lastMessageAt) : null,
  lastMessage: raw.lastMessage || null,
});

const loadThreads = async (page = 1) => {
  threadsLoading.value = true;
  threadsError.value = "";
  try {
    const payload = await fetchEmailThreadsPage({
      page,
      pageSize: THREADS_PAGE_SIZE,
      sort: sortOrder.value,
      deleted: includeDeleted.value ? "any" : false,
      assignedAdminIds: filterAssignedAdminIds.value,
    });
    items.value = (payload.items || []).map(parseThreadSummary);
    const p = payload.pagination || {};
    pagination.value = {
      page: p.page || page,
      pageSize: p.pageSize || THREADS_PAGE_SIZE,
      totalItems: p.totalItems || 0,
      totalPages: p.totalPages || 1,
    };
    currentPage.value = page;
  } catch (err) {
    threadsError.value = err?.message || "Failed to load threads.";
  } finally {
    threadsLoading.value = false;
  }
};

const toggleSortOrder = () => {
  sortOrder.value = sortOrder.value === "newest" ? "oldest" : "newest";
  loadThreads(1);
};

const toggleIncludeDeleted = () => {
  includeDeleted.value = !includeDeleted.value;
  loadThreads(1);
};

const patchThreadInList = (threadId, patch) => {
  const idx = items.value.findIndex((item) => item.id === threadId);
  if (idx !== -1) items.value[idx] = { ...items.value[idx], ...patch };
};

// ---------------------------------------------------------------------------
// Selected thread state
// ---------------------------------------------------------------------------
const selectedThreadId = ref(null);
const selectedThread = ref(null);
const messages = ref([]);
const matchedUser = ref(null);
const threadLoading = ref(false);
const threadError = ref("");

const replyHtml = ref("");
const replyText = computed(() => htmlToPlainText(replyHtml.value));
const replyFrom = ref("");
const replySending = ref(false);
const replyError = ref("");
const replySent = ref(false);

const selectThread = async (threadId) => {
  selectedThreadId.value = threadId;
  selectedThread.value = null;
  messages.value = [];
  matchedUser.value = null;
  replyHtml.value = "";
  replyFrom.value = defaultSender.value;
  replyError.value = "";
  replySent.value = false;
  assignError.value = "";
  assignMenuOpen.value = false;
  translateMenuOpen.value = false;
  translateError.value = "";
  activeTranslationLanguage.value = "";
  subjectTranslationsByLanguage.value = {};
  router.replace({ query: { ...route.query, threadId } });
  threadLoading.value = true;
  threadError.value = "";
  try {
    const payload = await fetchEmailThread(threadId);
    selectedThread.value = payload.thread;
    messages.value = payload.messages || [];
    matchedUser.value = payload.matchedUser || null;
    replyFrom.value = preferredSenderFor(payload.thread);
    patchThreadInList(threadId, {
      assignedAdmins: payload.thread?.assignedAdmins || [],
    });
  } catch (err) {
    threadError.value = err?.message || "Failed to load thread.";
  } finally {
    threadLoading.value = false;
  }
};

// Replies default to the mailbox a thread arrived at, keeping that
// conversation on the address it started on.
const preferredSenderFor = (thread) => {
  const mailboxSender = senders.value.find((sender) =>
    sameEmail(sender.email, thread?.mailbox)
  );
  return mailboxSender?.email || defaultSender.value;
};

// ---------------------------------------------------------------------------
// Assignees
// ---------------------------------------------------------------------------
const assignMenuOpen = ref(false);
const assignSaving = ref(false);
const assignError = ref("");

const selectedAssigneeIds = computed(
  () => selectedThread.value?.assignedAdminIds || []
);

const isAssignee = (adminId) => selectedAssigneeIds.value.includes(adminId);

const toggleAssignMenu = () => {
  assignedFilterMenuOpen.value = false;
  translateMenuOpen.value = false;
  assignMenuOpen.value = !assignMenuOpen.value;
};

const toggleAssignee = async (adminId) => {
  if (!selectedThreadId.value || assignSaving.value) return;
  const next = isAssignee(adminId)
    ? selectedAssigneeIds.value.filter((id) => id !== adminId)
    : [...selectedAssigneeIds.value, adminId];

  assignSaving.value = true;
  assignError.value = "";
  try {
    await setEmailThreadAssignees(selectedThreadId.value, next);
    const assignedAdmins = next.map((id) => ({
      id,
      name: adminsById.value[id]?.name || null,
      email: adminsById.value[id]?.email || null,
    }));
    selectedThread.value = {
      ...selectedThread.value,
      assignedAdminIds: next,
      assignedAdmins,
    };
    patchThreadInList(selectedThreadId.value, { assignedAdmins });
  } catch (err) {
    assignError.value = err?.message || "Failed to update the assignees.";
  } finally {
    assignSaving.value = false;
  }
};

// ---------------------------------------------------------------------------
// Translate
// ---------------------------------------------------------------------------
const translateMenuOpen = ref(false);
const translateLoading = ref(false);
const translateError = ref("");
const activeTranslationLanguage = ref("");
const translationsByLanguage = reactive({}); // language -> { messageId -> text }
const subjectTranslationsByLanguage = ref({}); // language -> subject text

const toggleTranslateMenu = () => {
  assignedFilterMenuOpen.value = false;
  assignMenuOpen.value = false;
  translateMenuOpen.value = !translateMenuOpen.value;
};

const translatableMessages = computed(() =>
  messages.value
    .filter((message) => messageBody(message).trim())
    .slice(-TRANSLATION_MESSAGE_LIMIT)
);

const translationFor = (message) => {
  if (!activeTranslationLanguage.value || !message) return null;
  const cache = translationsByLanguage[activeTranslationLanguage.value];
  return cache ? cache[message.id] || null : null;
};

const threadSubject = computed(() => (selectedThread.value?.subject || "").trim());

const translatedSubject = computed(() => {
  if (!activeTranslationLanguage.value) return null;
  return subjectTranslationsByLanguage.value[activeTranslationLanguage.value] || null;
});

const translateThread = async (language) => {
  translateMenuOpen.value = false;
  translateError.value = "";
  activeTranslationLanguage.value = language;

  const cache =
    translationsByLanguage[language] || (translationsByLanguage[language] = {});
  const pending = translatableMessages.value.filter(
    (message) => !(message.id in cache)
  );
  const subjectPending =
    !!threadSubject.value && !(language in subjectTranslationsByLanguage.value);
  if (pending.length === 0 && !subjectPending) return;

  translateLoading.value = true;
  try {
    const [messagesPayload, subjectPayload] = await Promise.all([
      pending.length
        ? fetchEmailMessagesTranslation(
          pending.map((message) => message.id),
          language
        )
        : null,
      subjectPending
        ? fetchFreeTextTranslation(threadSubject.value, language)
        : null,
    ]);
    const translations = Array.isArray(messagesPayload?.translations)
      ? messagesPayload.translations
      : [];
    translations.forEach((translation) => {
      if (translation?.messageId) {
        cache[translation.messageId] = translation.translatedText;
      }
    });
    const translatedSubjectText =
      subjectPayload?.translations?.[0]?.translatedText;
    if (translatedSubjectText) {
      subjectTranslationsByLanguage.value = {
        ...subjectTranslationsByLanguage.value,
        [language]: translatedSubjectText,
      };
    }
  } catch (err) {
    translateError.value = err?.message || "Failed to translate messages.";
  } finally {
    translateLoading.value = false;
  }
};

const clearTranslation = () => {
  activeTranslationLanguage.value = "";
  translateError.value = "";
  translateMenuOpen.value = false;
};

// ---------------------------------------------------------------------------
// Send reply
// ---------------------------------------------------------------------------
const sendReply = async () => {
  const text = replyText.value.trim();
  if (!text || !selectedThreadId.value) return;
  replySending.value = true;
  replyError.value = "";
  try {
    await sendEmailReply(selectedThreadId.value, {
      bodyText: text,
      bodyHtml: finalizeEmailHtml(replyHtml.value),
      fromEmail: replyFrom.value || null,
    });
    await selectThread(selectedThreadId.value);
    replySent.value = true;
  } catch (err) {
    replyError.value = err?.message || "Failed to send reply.";
  } finally {
    replySending.value = false;
  }
};

const composeModalOpen = ref(false);
const composeFrom = ref("");
const composeToName = ref("");
const composeToEmail = ref("");
const composeSubject = ref("");
const composeHtml = ref("");
const composeBodyText = computed(() => htmlToPlainText(composeHtml.value));
const composeSending = ref(false);
const composeError = ref("");

const canSendCompose = computed(
  () =>
    Boolean(composeToEmail.value.trim()) &&
    Boolean(composeSubject.value.trim()) &&
    Boolean(composeBodyText.value.trim())
);

const openComposeModal = () => {
  if (!composeFrom.value) composeFrom.value = defaultSender.value;
  composeError.value = "";
  composeModalOpen.value = true;
};

const closeComposeModal = () => {
  if (composeSending.value) return;
  composeModalOpen.value = false;
};

const resetComposeForm = () => {
  composeToName.value = "";
  composeToEmail.value = "";
  composeSubject.value = "";
  composeHtml.value = "";
  composeError.value = "";
};

const sendCompose = async () => {
  if (!canSendCompose.value || composeSending.value) return;
  composeSending.value = true;
  composeError.value = "";
  try {
    const result = await sendNewEmail({
      toEmail: composeToEmail.value.trim(),
      toName: composeToName.value.trim() || null,
      subject: composeSubject.value.trim(),
      bodyText: composeBodyText.value.trim(),
      bodyHtml: finalizeEmailHtml(composeHtml.value),
      fromEmail: composeFrom.value || null,
    });
    composeModalOpen.value = false;
    resetComposeForm();
    await loadThreads(1);
    if (result?.threadId) await selectThread(result.threadId);
  } catch (err) {
    composeError.value = err?.message || "Failed to send the email.";
  } finally {
    composeSending.value = false;
  }
};

// ---------------------------------------------------------------------------
// Delete thread
// ---------------------------------------------------------------------------
const deleting = ref(false);
const deleteError = ref("");

const deleteSelectedThread = async () => {
  if (!selectedThreadId.value) return;
  deleting.value = true;
  deleteError.value = "";
  try {
    await deleteEmailThread(selectedThreadId.value);
    const deletedAt = new Date().toISOString();
    patchThreadInList(selectedThreadId.value, { isDeleted: true, deletedAt });
    selectedThread.value = { ...selectedThread.value, isDeleted: true, deletedAt };
  } catch (err) {
    deleteError.value = err?.message || "Failed to delete the thread.";
  } finally {
    deleting.value = false;
  }
};

const restoring = ref(false);
const restoreError = ref("");

const restoreSelectedThread = async () => {
  if (!selectedThreadId.value) return;

  restoring.value = true;
  restoreError.value = "";
  try {
    await restoreEmailThread(selectedThreadId.value);
    patchThreadInList(selectedThreadId.value, { isDeleted: false, deletedAt: null });
    selectedThread.value = { ...selectedThread.value, isDeleted: false, deletedAt: null };
  } catch (err) {
    restoreError.value = err?.message || "Failed to restore the thread.";
  } finally {
    restoring.value = false;
  }
};

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------
const downloadingAttachment = ref("");
const attachmentError = ref("");

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const downloadAttachment = async (message, attachment) => {
  if (!attachment.contentAvailable) return;
  const key = `${message.id}-${attachment.index}`;
  downloadingAttachment.value = key;
  attachmentError.value = "";
  try {
    const blob = await fetchEmailAttachment(
      selectedThreadId.value,
      message.id,
      attachment.index
    );
    saveBlob(blob, attachment.filename || "attachment");
  } catch (err) {
    attachmentError.value = err?.message || "Failed to download the attachment.";
  } finally {
    downloadingAttachment.value = "";
  }
};

// ---------------------------------------------------------------------------
// Suggest reply modal
// ---------------------------------------------------------------------------
const suggestModalOpen = ref(false);
const suggestAdminInput = ref("");
const suggestions = ref([]);
const suggestLoading = ref(false);
const suggestError = ref("");

const openSuggestModal = () => {
  suggestions.value = [];
  suggestError.value = "";
  suggestAdminInput.value = replyText.value.trim();
  suggestModalOpen.value = true;
};

const closeSuggestModal = () => {
  suggestModalOpen.value = false;
  suggestAdminInput.value = "";
  suggestions.value = [];
  suggestError.value = "";
};

const suggestReply = async () => {
  if (!selectedThreadId.value) return;
  suggestLoading.value = true;
  suggestError.value = "";
  suggestions.value = [];
  try {
    const result = await fetchEmailReplySuggestions(selectedThreadId.value, {
      adminInput: suggestAdminInput.value.trim() || null,
    });
    suggestions.value = result.suggestions || [];
  } catch (err) {
    suggestError.value = err?.message || "Failed to fetch suggestions.";
  } finally {
    suggestLoading.value = false;
  }
};

const applySuggestion = (text) => {
  replyHtml.value = plainTextToHtml(text);
  closeSuggestModal();
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  const h = `${d.getHours()}`.padStart(2, "0");
  const m = `${d.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${h}:${m}`;
};

const formatBytes = (size) => {
  if (!size || size < 1024) return `${size || 0} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const addressLabel = (address) => {
  if (!address) return "Unknown sender";
  return address.name ? `${address.name} <${address.email}>` : address.email;
};

// Email HTML is untrusted, so it is never rendered as markup — the text is
// extracted from a detached document and shown as plain text instead.
const htmlToText = (html) => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body?.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  } catch (err) {
    return html;
  }
};

const messageBody = (message) => {
  if (message.textPlain && message.textPlain.trim()) return message.textPlain;
  if (message.textHtml) return htmlToText(message.textHtml);
  return "";
};

const messageSender = (message) => {
  if (message.direction !== "outbound") return addressLabel(message.from);
  const name =
    message.sentByAdminName || (message.from ? addressLabel(message.from) : "You");
  const isMe =
    Boolean(message.sentByAdminId) && message.sentByAdminId === currentAdminId.value;
  return isMe ? `${name} (you)` : name;
};

const failedChecks = (message) => {
  const auth = message.authentication;
  if (!auth || message.direction === "outbound") return [];
  return ["spf", "dkim", "dmarc"].filter(
    (check) => auth[check] && auth[check] !== "pass" && auth[check] !== "none"
  );
};

const previewText = (thread) => {
  const preview = thread.lastMessage?.preview;
  if (!preview) return "";
  if (thread.lastMessage.direction !== "outbound") return preview;
  return `${thread.lastMessage.sentByAdminName || "You"}: ${preview}`;
};

// ---------------------------------------------------------------------------
// Close open dropdown menus on an outside click
// ---------------------------------------------------------------------------
const closeMenus = () => {
  assignMenuOpen.value = false;
  translateMenuOpen.value = false;
  assignedFilterMenuOpen.value = false;
};

onMounted(() => document.addEventListener("click", closeMenus));
onUnmounted(() => document.removeEventListener("click", closeMenus));

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------
onMounted(async () => {
  await checkStatus();
  if (configured.value) {
    await loadAdmins();
    await loadThreads(1);
    const threadIdFromQuery = route.query.threadId;
    if (threadIdFromQuery) {
      await selectThread(threadIdFromQuery);
    }
  }
});
</script>

<template>
  <div class="container-fluid py-4">
    <!-- Not configured -->
    <div v-if="statusChecked && !configured" class="card">
      <div class="card-body text-center text-secondary py-5">
        <i class="ni ni-email-83 text-4xl d-block mb-3" style="font-size:3rem;opacity:.3"></i>
        <p class="mb-2">The Email Inbox isn't configured yet.</p>
        <p class="text-sm mb-3">
          Set the inbound webhook secret — or the authorization header — on the Config page, matching the
          <code>WEBHOOK_SECRET</code> / <code>WEBHOOK_AUTHORIZATION</code> of the Cloudflare email router worker.
        </p>
        <router-link to="/config" class="btn btn-sm btn-primary">Go to Config</router-link>
        <div v-if="statusError" class="alert alert-danger text-sm py-2 mt-3 mb-0">{{ statusError }}</div>
      </div>
    </div>

    <div v-else class="row">
      <!-- ===== LEFT: Thread list ===== -->
      <div class="col-12 col-lg-4 col-xl-3 mb-4 mb-lg-0">
        <div class="card h-100">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="mb-0">
                Threads
                <span v-if="!threadsLoading" class="text-xs text-secondary fw-normal">({{ pagination.totalItems
                  }})</span>
              </h6>
              <div class="d-flex gap-1">
                <button class="btn btn-sm mb-0 px-2" :class="includeDeleted ? 'btn-primary' : 'btn-outline-secondary'"
                  :disabled="threadsLoading" @click="toggleIncludeDeleted"
                  :title="includeDeleted ? 'Showing deleted threads too — click to hide them' : 'Deleted threads hidden — click to show them'">
                  Deleted
                </button>
                <button class="btn btn-sm btn-outline-secondary mb-0 px-2" :disabled="threadsLoading"
                  @click="toggleSortOrder"
                  :title="sortOrder === 'newest' ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'">
                  <i class="ni" :class="sortOrder === 'newest' ? 'ni-bold-down' : 'ni-bold-up'"></i>
                </button>
                <button class="btn btn-sm btn-outline-secondary mb-0 px-2" @click="loadThreads(currentPage)"
                  :disabled="threadsLoading" title="Refresh">
                  <span v-if="threadsLoading" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="fas fa-sync"></i>
                </button>
              </div>
            </div>

            <button v-if="canReply" class="btn btn-primary btn-sm w-100 mb-2" @click="openComposeModal">
              <i class="ni ni-fat-add me-1"></i>New email
            </button>

            <div class="d-flex align-items-center gap-2 mb-2 position-relative" @click.stop>
              <label class="text-xs text-secondary mb-0 flex-shrink-0">Assigned</label>
              <button
                class="btn btn-sm btn-outline-secondary mb-0 py-1 px-2 text-xs text-truncate flex-grow-1 text-start"
                :disabled="threadsLoading" @click="toggleAssignedFilterMenu">
                {{ assignedFilterLabel }}
              </button>
              <button v-if="!isAssignedFilterDefault" class="btn btn-sm btn-outline-secondary mb-0 py-1 px-2 text-xs"
                :disabled="threadsLoading" @click="resetAssignedFilter" title="Back to unassigned + mine">
                Reset
              </button>

              <div v-if="assignedFilterMenuOpen"
                class="position-absolute bg-white shadow-sm rounded-2 border py-1 assign-filter-dropdown">
                <label class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0"
                  style="cursor:pointer">
                  <input type="checkbox" :checked="isAssignedFilterSelected(null)"
                    @change="toggleAssignedFilterValue(null)" />
                  Unassigned
                </label>
                <label v-if="currentAdminId"
                  class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0" style="cursor:pointer">
                  <input type="checkbox" :checked="isAssignedFilterSelected(currentAdminId)"
                    @change="toggleAssignedFilterValue(currentAdminId)" />
                  Me ({{ store.getters.currentAdmin?.name }})
                </label>

                <template v-if="canViewAll">
                  <div v-if="admins.length" class="dropdown-divider my-1"></div>
                  <label v-for="admin in admins.filter((a) => a.id !== currentAdminId)" :key="admin.id"
                    class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0" style="cursor:pointer">
                    <input type="checkbox" :checked="isAssignedFilterSelected(admin.id)"
                      @change="toggleAssignedFilterValue(admin.id)" />
                    {{ admin.name }}
                  </label>
                  <div class="dropdown-divider my-1"></div>
                  <button
                    class="dropdown-item text-sm py-1 px-2 text-secondary bg-transparent border-0 w-100 text-start"
                    @click="clearAssignedFilter">
                    Show all threads
                  </button>
                </template>
              </div>
            </div>
          </div>

          <div class="card-body p-0">
            <div v-if="threadsLoading" class="p-3 text-center text-secondary text-sm">Loading…</div>

            <div v-else-if="threadsError" class="p-3">
              <div class="alert alert-danger text-sm py-2 mb-0">{{ threadsError }}</div>
            </div>

            <div v-else-if="items.length === 0" class="p-3 text-center text-secondary text-sm">
              No threads found.
            </div>

            <ul v-else class="list-group list-group-flush">
              <li v-for="item in items" :key="item.id" class="list-group-item list-group-item-action px-3 py-2"
                :class="{ active: selectedThreadId === item.id, 'thread-deleted': item.isDeleted }"
                style="cursor: pointer" @click="selectThread(item.id)">
                <div class="d-flex align-items-center justify-content-between">
                  <span class="text-sm text-truncate fw-semibold" style="max-width:150px">
                    {{ item.participant?.name || item.participant?.email || "Unknown sender" }}
                  </span>
                  <span class="text-xs text-secondary ms-1 flex-shrink-0">{{ formatDate(item.lastMessageAt) }}</span>
                </div>
                <div class="text-xs text-truncate"
                  :class="selectedThreadId === item.id ? 'text-white-50' : 'text-secondary'">
                  {{ item.subject }}
                </div>
                <div v-if="previewText(item)" class="text-xxs text-truncate"
                  :class="selectedThreadId === item.id ? 'text-white-50' : 'text-secondary'">
                  {{ previewText(item) }}
                </div>
                <div class="d-flex align-items-center gap-1 mt-1">
                  <span v-if="item.messageCount > 1" class="badge badge-sm bg-gradient-secondary">{{ item.messageCount
                    }}</span>
                  <span v-if="item.isDeleted" class="badge badge-sm bg-gradient-danger">Deleted</span>
                  <span v-if="item.lastMessage?.attachmentCount" class="badge badge-sm bg-gradient-light text-dark">
                    <i class="ni ni-single-copy-04"></i> {{ item.lastMessage.attachmentCount }}
                  </span>
                  <span v-for="assignee in item.assignedAdmins" :key="assignee.id"
                    class="badge badge-sm bg-gradient-primary text-truncate" style="max-width:110px">
                    {{ assigneeLabel(assignee) }}
                  </span>
                </div>
              </li>
            </ul>

            <div v-if="!threadsLoading && pagination.totalPages > 1"
              class="px-3 py-2 d-flex justify-content-between align-items-center border-top">
              <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="currentPage <= 1"
                @click="loadThreads(currentPage - 1)">
                ← Prev
              </button>
              <span class="text-xs text-secondary">{{ currentPage }} / {{ pagination.totalPages }}</span>
              <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="currentPage >= pagination.totalPages"
                @click="loadThreads(currentPage + 1)">
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== MIDDLE: Thread detail ===== -->
      <div class="col-12 col-lg-8 col-xl-6 mb-4 mb-xl-0">
        <div v-if="!selectedThreadId" class="card">
          <div class="card-body text-center text-secondary py-5">
            <i class="ni ni-email-83 text-4xl d-block mb-3" style="font-size:3rem;opacity:.3"></i>
            Select a thread to view the conversation.
          </div>
        </div>

        <div v-else class="card d-flex flex-column" style="min-height:70vh">
          <div v-if="threadLoading" class="card-body text-center text-secondary py-5">Loading…</div>

          <div v-else-if="threadError" class="card-body">
            <div class="alert alert-danger text-sm py-2 mb-0">{{ threadError }}</div>
          </div>

          <template v-else-if="selectedThread">
            <!-- Header -->
            <div class="card-header pb-2">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <h6 class="mb-0">{{ selectedThread.subject || "(no subject)" }}</h6>
                <span v-if="selectedThread.isDeleted" class="badge bg-gradient-danger">Deleted</span>
              </div>
              <p v-if="translatedSubject" class="text-xs mb-0 translated-subject">
                <span class="fw-semibold text-secondary">{{ activeTranslationLanguage }}: </span>{{ translatedSubject }}
              </p>
              <p class="text-xs text-secondary mb-0">
                {{ addressLabel(selectedThread.participant) }} · to {{ selectedThread.mailbox }} ·
                {{ selectedThread.messageCount }} message(s)
              </p>
              <p v-if="selectedThread.isDeleted" class="text-xs text-danger mb-0">
                Deleted {{ formatDate(selectedThread.deletedAt) }}
              </p>
            </div>

            <!-- Toolbar -->
            <div class="card-body py-2 thread-toolbar">
              <div v-if="assignError" class="text-danger text-xs mb-1">{{ assignError }}</div>
              <div v-if="deleteError" class="text-danger text-xs mb-1">{{ deleteError }}</div>
              <div v-if="restoreError" class="text-danger text-xs mb-1">{{ restoreError }}</div>
              <div class="d-flex align-items-center flex-wrap gap-2">
                <!-- Assignee -->
                <div class="d-flex align-items-center gap-1 flex-wrap position-relative" @click.stop>
                  <span v-if="!selectedThread.assignedAdmins?.length"
                    class="badge badge-sm bg-gradient-secondary">Unassigned</span>
                  <span v-for="assignee in selectedThread.assignedAdmins" :key="assignee.id"
                    class="badge badge-sm bg-gradient-primary">
                    {{ assigneeLabel(assignee) }}
                  </span>
                  <button v-if="canAssign && !selectedThread.isDeleted"
                    class="btn btn-icon-only btn-sm btn-outline-secondary mb-0" :disabled="assignSaving"
                    title="Change assignee" @click="toggleAssignMenu">
                    <span v-if="assignSaving" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="ni ni-single-02"></i>
                  </button>

                  <div v-if="assignMenuOpen"
                    class="position-absolute bg-white shadow-sm rounded-2 border py-1 assign-filter-dropdown">
                    <label v-for="admin in admins" :key="admin.id"
                      class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0"
                      style="cursor:pointer">
                      <input type="checkbox" :checked="isAssignee(admin.id)" :disabled="assignSaving"
                        @change="toggleAssignee(admin.id)" />
                      {{ admin.name }}
                    </label>
                    <div v-if="!admins.length" class="text-xs text-secondary px-2 py-1">
                      No assignable admins.
                    </div>
                  </div>
                </div>

                <div class="toolbar-divider"></div>

                <!-- Actions -->
                <div class="d-flex align-items-center gap-1">
                  <div v-if="canTranslate" class="position-relative" @click.stop>
                    <button class="btn btn-icon-only btn-sm mb-0"
                      :class="activeTranslationLanguage ? 'btn-primary' : 'btn-outline-secondary'"
                      :disabled="translateLoading"
                      :title="activeTranslationLanguage ? `Translated: ${activeTranslationLanguage}` : 'Translate'"
                      @click="toggleTranslateMenu">
                      <span v-if="translateLoading" class="spinner-border spinner-border-sm"></span>
                      <span v-else>🌐</span>
                    </button>
                    <div v-if="translateMenuOpen"
                      class="position-absolute bg-white shadow-sm rounded-2 border py-1 translate-dropdown">
                      <button v-for="language in TRANSLATION_LANGUAGES" :key="language.code"
                        class="dropdown-item text-sm py-1 px-2"
                        :class="{ 'fw-bold': activeTranslationLanguage === language.code }"
                        @click="translateThread(language.code)">
                        {{ language.label }}
                      </button>
                      <template v-if="activeTranslationLanguage">
                        <div class="dropdown-divider my-1"></div>
                        <button class="dropdown-item text-sm py-1 px-2 text-secondary" @click="clearTranslation">
                          Hide translation
                        </button>
                      </template>
                    </div>
                  </div>
                  <button v-if="canDelete && !selectedThread.isDeleted"
                    class="btn btn-icon-only btn-sm btn-outline-danger mb-0" :disabled="deleting" title="Delete"
                    @click="deleteSelectedThread">
                    <span v-if="deleting" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="ni ni-fat-remove"></i>
                  </button>
                  <button v-if="canDelete && selectedThread.isDeleted"
                    class="btn btn-icon-only btn-sm btn-outline-success mb-0" :disabled="restoring" title="Restore"
                    @click="restoreSelectedThread">
                    <span v-if="restoring" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="ni ni-fat-add"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Conversation -->
            <div class="card-body flex-grow-1 overflow-auto" style="max-height:45vh;min-height:180px">
              <div v-if="attachmentError" class="alert alert-danger text-sm py-2">{{ attachmentError }}</div>
              <div v-if="translateError" class="alert alert-danger text-sm py-2">{{ translateError }}</div>

              <div v-for="message in messages" :key="message.id" class="mb-3 d-flex"
                :class="message.direction === 'outbound' ? 'justify-content-end' : 'justify-content-start'">
                <div class="email-bubble" :class="message.direction === 'outbound' ? 'outbound' : 'inbound'">
                  <div class="d-flex align-items-center justify-content-between gap-3 mb-1">
                    <span class="text-xs fw-semibold">{{ messageSender(message) }}</span>
                    <span class="text-xxs text-secondary">{{ formatDate(message.sentAt) }}</span>
                  </div>

                  <div v-if="failedChecks(message).length" class="mb-1 d-flex gap-1">
                    <span v-for="check in failedChecks(message)" :key="check"
                      class="badge badge-sm bg-gradient-warning text-dark">
                      {{ check.toUpperCase() }}: {{ message.authentication[check] }}
                    </span>
                  </div>

                  <div class="text-sm" style="white-space:pre-wrap">{{ messageBody(message) }}</div>

                  <div v-if="translationFor(message)" class="mt-2 pt-2 text-xs translation-block"
                    style="white-space:pre-wrap">
                    <span class="fw-semibold text-secondary">{{ activeTranslationLanguage }}: </span>{{
                      translationFor(message) }}
                  </div>

                  <div v-if="message.attachments?.length" class="mt-2 d-flex flex-wrap gap-2">
                    <button v-for="attachment in message.attachments" :key="attachment.index"
                      class="btn btn-sm btn-outline-secondary mb-0 py-1 px-2 text-xs"
                      :disabled="!attachment.contentAvailable || downloadingAttachment === `${message.id}-${attachment.index}`"
                      :title="attachment.contentAvailable ? 'Download' : 'Content was not stored'"
                      @click="downloadAttachment(message, attachment)">
                      <i class="ni ni-single-copy-04 me-1"></i>
                      {{ attachment.filename || "attachment" }}
                      <span class="text-secondary">({{ formatBytes(attachment.size) }})</span>
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="messages.length === 0" class="text-sm text-secondary fst-italic">
                This thread has no messages.
              </div>
            </div>

            <!-- Reply form -->
            <div v-if="selectedThread.isDeleted" class="card-footer pt-2 pb-3 text-sm text-secondary fst-italic">
              This thread is deleted — replies are disabled.
            </div>

            <div v-else class="card-footer pt-2 pb-3">
              <div v-if="replyError" class="alert alert-danger text-sm py-2 mb-2">{{ replyError }}</div>
              <div v-if="replySent" class="alert alert-success text-sm py-2 mb-2">Reply sent.</div>

              <div v-if="senders.length" class="d-flex align-items-center gap-2 mb-2">
                <label class="text-xs text-secondary mb-0 flex-shrink-0">Reply from</label>
                <select v-model="replyFrom" class="form-select form-select-sm" :disabled="replySending"
                  style="max-width:320px">
                  <option v-for="sender in senders" :key="sender.email" :value="sender.email">
                    {{ senderLabel(sender) }}
                  </option>
                </select>
              </div>

              <div class="d-flex gap-2 align-items-end">
                <RichTextEditor v-model="replyHtml" class="flex-grow-1" placeholder="Type a reply…"
                  min-height="80px" :disabled="replySending" />
                <div class="d-flex flex-column gap-1 flex-shrink-0">
                  <button class="btn btn-outline-secondary btn-sm mb-0" :disabled="replySending"
                    @click="openSuggestModal" title="Get AI reply suggestions">
                    ✨ Suggest
                  </button>
                  <button v-if="canReply" class="btn btn-primary btn-sm mb-0"
                    :disabled="replySending || !replyText.trim()" @click="sendReply">
                    <span v-if="replySending">Sending…</span>
                    <span v-else><i class="ni ni-send me-1"></i>Send</span>
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- ===== RIGHT: User details ===== -->
      <div class="col-12 col-xl-3">
        <UserDetailsPanel :user-id="matchedUser?.id || null" :fallback-name="matchedUser?.name || ''"
          empty-message="No matching user for this email address." />
      </div>
    </div>

    <!-- ===== Compose Modal ===== -->
    <Teleport to="body">
      <div v-if="composeModalOpen" class="suggest-modal-backdrop">
        <div class="compose-modal-dialog card shadow-lg">
          <div class="card-header pb-2 d-flex align-items-center justify-content-between">
            <h6 class="mb-0">New email</h6>
            <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="composeSending"
              @click="closeComposeModal">✕</button>
          </div>

          <div class="card-body">
            <div v-if="senders.length" class="mb-2">
              <label class="form-label text-xs fw-semibold text-secondary mb-1">From</label>
              <select v-model="composeFrom" class="form-select form-select-sm" :disabled="composeSending">
                <option v-for="sender in senders" :key="sender.email" :value="sender.email">
                  {{ senderLabel(sender) }}
                </option>
              </select>
            </div>

            <div class="row g-2 mb-2">
              <div class="col-12 col-sm-7">
                <label class="form-label text-xs fw-semibold text-secondary mb-1">To</label>
                <input v-model="composeToEmail" type="email" class="form-control form-control-sm"
                  placeholder="name@example.com" :disabled="composeSending" />
              </div>
              <div class="col-12 col-sm-5">
                <label class="form-label text-xs fw-semibold text-secondary mb-1">Name <span
                    class="fw-normal">(optional)</span></label>
                <input v-model="composeToName" type="text" class="form-control form-control-sm"
                  :disabled="composeSending" />
              </div>
            </div>

            <div class="mb-2">
              <label class="form-label text-xs fw-semibold text-secondary mb-1">Subject</label>
              <input v-model="composeSubject" type="text" class="form-control form-control-sm"
                :disabled="composeSending" />
            </div>

            <div class="mb-3">
              <label class="form-label text-xs fw-semibold text-secondary mb-1">Message</label>
              <RichTextEditor v-model="composeHtml" min-height="180px" :disabled="composeSending" />
            </div>

            <div v-if="composeError" class="alert alert-danger text-sm py-2 mb-3">{{ composeError }}</div>

            <button class="btn btn-primary btn-sm mb-0 w-100" :disabled="composeSending || !canSendCompose"
              @click="sendCompose">
              <span v-if="composeSending">Sending…</span>
              <span v-else><i class="ni ni-send me-1"></i>Send</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ===== Suggest Reply Modal ===== -->
    <Teleport to="body">
      <div v-if="suggestModalOpen" class="suggest-modal-backdrop" @click.self="closeSuggestModal">
        <div class="suggest-modal-dialog card shadow-lg">
          <div class="card-header pb-2 d-flex align-items-center justify-content-between">
            <h6 class="mb-0">✨ AI Reply Suggestions</h6>
            <button class="btn btn-sm btn-outline-secondary mb-0" @click="closeSuggestModal">✕</button>
          </div>

          <div class="card-body">
            <div class="mb-3">
              <label class="form-label text-xs fw-semibold text-secondary mb-1">Admin input <span
                  class="fw-normal">(optional — guide the AI)</span></label>
              <textarea v-model="suggestAdminInput" class="form-control form-control-sm" rows="2"
                placeholder="e.g. Explain how to reset their password" :disabled="suggestLoading"
                style="resize:none"></textarea>
            </div>

            <button class="btn btn-primary btn-sm mb-0 w-100" :disabled="suggestLoading" @click="suggestReply">
              <span v-if="suggestLoading">Generating…</span>
              <span v-else>Get suggestions</span>
            </button>

            <div v-if="suggestError" class="alert alert-danger text-sm py-2 mt-3 mb-0">{{ suggestError }}</div>

            <div v-if="suggestions.length > 0" class="mt-3 d-flex flex-column gap-3">
              <div v-for="(s, i) in suggestions" :key="i" class="border rounded p-2">
                <div class="text-xs text-secondary fw-semibold mb-2">Suggestion {{ i + 1 }}</div>
                <div class="d-flex flex-column gap-2">
                  <div v-for="(text, lang) in s" :key="lang">
                    <div class="text-xxs text-secondary fw-bold mb-1">{{ lang.toUpperCase() }}</div>
                    <button class="btn btn-outline-secondary btn-sm mb-0 text-start w-100"
                      style="white-space:pre-wrap;text-align:left;font-size:13px;line-height:1.5"
                      @click="applySuggestion(text)">
                      {{ text }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.thread-toolbar {
  flex: 0 0 auto;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
}

.toolbar-divider {
  width: 1px;
  align-self: stretch;
  background-color: #dee2e6;
}

.list-group-item.active {
  background-color: #5e72e4;
  border-color: #5e72e4;
  color: #fff;
}

.list-group-item.active .text-secondary,
.list-group-item.active .text-muted {
  color: rgba(255, 255, 255, 0.65) !important;
}

.list-group-item-action:hover:not(.active) {
  background-color: #f8f9fa;
}

.list-group-item.thread-deleted:not(.active) {
  opacity: 0.6;
}

.email-bubble {
  max-width: 85%;
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  background-color: #fff;
}

.email-bubble.outbound {
  background-color: #f0f3ff;
  border-color: #d7ddff;
}

.translate-dropdown {
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 140px;
}

.translation-block {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.translated-subject {
  color: #344767;
}

.assign-filter-dropdown {
  top: 100%;
  left: 0;
  z-index: 1000;
  min-width: 200px;
  max-height: 260px;
  overflow-y: auto;
}

.suggest-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.suggest-modal-dialog,
.compose-modal-dialog {
  width: 100%;
  max-width: 520px;
  max-height: 85vh;
  overflow-y: auto;
}

.compose-modal-dialog {
  max-width: 600px;
}
</style>
